import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import { useMemo, useState, useRef, useEffect } from "react";
import { Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, Grid, LinearProgress, Menu, Paper, Radio, RadioGroup } from "@mui/material";
import moment from "moment-timezone";
import ChartUnSelected from "../../../asset/image/ChartUnSelected.svg";
import { getTimezoneCity } from "../../../utils/util";
import Card from "@mui/material/Card";
import { makeStyles } from "@material-ui/core/styles";
import Download from '../../../asset/image/DownloadUsage.svg';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';
import TuneIcon from '@mui/icons-material/Tune';
import close from "../../../asset/image/close.svg";
import _ from "lodash";
import AnalyticsCardContents from "../../../UserScreen/AnalyticsDashboard/AnalyticsCard";

NoDataToDisplay(Highcharts);

const AssetUsageBarChart = (props) => {
    const { startDate, endDate, assetUsage, assetUsageSplit, userTimezone, newSummaryServiceLine, selectedFilters, defaultFilters, handleFilterChange, includeInactiveServiceLines, onIncludeInactiveServiceLinesChange } = props;
    const timeZone = getTimezoneCity(userTimezone);
    const [anchorElRef, setAnchorElRef] = useState<HTMLElement>();
    const [filterPopup, setFilterPopup] = useState(false);
    const [selectedCards, setSelectedCards] = useState<any[]>([
        { 0: { heading: "Total", rightValue: 0, showProgressBar: false } },
        { 1: { heading: "Global Priority", rightValue: 0, showProgressBar: false } },
        { 2: { heading: 'Local Priority', rightValue: 0, showProgressBar: false } },
        { 3: { heading: 'Mobile Priority', rightValue: 0, showProgressBar: false } },
        { 4: { heading: 'Other Data', rightValue: 0, showProgressBar: false } },
        { 4: { heading: 'Legacy Data', rightValue: 0, showProgressBar: false } },
        ]);

    const formattedData = useMemo(() => {
        const data: any = {
            global_priority: [],
            local_priority: [],
            mobile_priority: [],
            other_data: [],
            legacy_plans_data: [],
        };

        const cards: any = [
            { key: 'global_priority', label: 'GLOBAL PRIORITY' },
            { key: 'local_priority', label: 'LOCAL PRIORITY' },
            { key: 'mobile_priority_data', label: 'MOBILE PRIORITY' },
            { key: 'other_data', label: 'OTHER DATA' },
            { key: 'legacy_plans_data', label: 'LEGACY DATA' },
            { key: 'total_consumed_global_priority_topup_gb', label: 'Global Priority GB' },
            { key: 'total_consumed_local_priority_topup_gb', label: 'Local Priority GB' }
        ];

        assetUsageSplit &&
            Object.keys(assetUsageSplit)?.forEach((key) => {
                const series = assetUsageSplit[key];

                series?.forEach(({ x, y }) => {
                    const timestamp = x;
                    const usage_value = y;

                    if (key === "global_priority") data.global_priority.push([timestamp, usage_value]);
                    if (key === "local_priority") data.local_priority.push([timestamp, usage_value]);
                    if (key === "mobile_priority_data") data.mobile_priority.push([timestamp, usage_value]);
                    if (key === "legacy_plans_data") data.legacy_plans_data.push([timestamp, usage_value]);
                    if (key === "other_data") data.other_data.push([timestamp, usage_value]);
                });
            });

        // Create cardsData using the first `y` value in the array for each key (total value)
        const cardsData = cards.map(({ key, label }) => {
            const series = assetUsageSplit?.[key];
            const total = series && series.length > 0
                            ? series?.reduce((sum, item) => sum + Number(item?.y || 0), 0)?.toFixed(2)
                            : '0.00';
            return {
                label: label,
                value: total
            };
        });
    
        return { data, cardsData };
    }, [assetUsage, assetUsageSplit]);

    const HighchartsInstance = { ...Highcharts };
    Exporting(HighchartsInstance);
    ExportData(HighchartsInstance);
    Highcharts.setOptions({
        exporting: { enabled: false },
    });
    interface SeriesItem {
        name: string;
        data: any;
        stack: string;
        color: string;
    }

    const [series, setSeries] = useState<SeriesItem[]>([]);
    useEffect(() => {
        if (!selectedFilters) {
            setSeries([]);
            return;
        }

        const filterMap: Record<string, SeriesItem[]> = {
            standard: [
                { name: "Global Priority", data: formattedData.data.global_priority, stack: "gb", color: "#3a88d3" },
                { name: "Local Priority", data: formattedData.data.local_priority, stack: "gb", color: "#4464f3" },
                { name: "Mobile Priority", data: formattedData.data.mobile_priority, stack: "gb", color: "#1d3fa6" },
                { name: "Legacy Plans", data: formattedData.data.legacy_plans_data, stack: "gb", color: "#6b7f99" },
                { name: "Other Data", data: formattedData.data.other_data, stack: "gb", color: "#8ac9e3" },
            ],

            total: [
                { name: "Global Priority", data: formattedData.data.global_priority, stack: "gb", color: "#3a88d3" },
                { name: "Local Priority", data: formattedData.data.local_priority, stack: "gb", color: "#4464f3" },
                { name: "Mobile Priority", data: formattedData.data.mobile_priority, stack: "gb", color: "#1d3fa6" },
                { name: "Legacy Plans", data: formattedData.data.legacy_plans_data, stack: "gb", color: "#6b7f99" },
                { name: "Other Data", data: formattedData.data.other_data, stack: "gb", color: "#8ac9e3" },
            ]
        };



        const updatedSeries: SeriesItem[] = filterMap[selectedFilters] || [];
        setSeries(updatedSeries);
    }, [selectedFilters, formattedData]);

    useEffect(() => {
        if (!formattedData?.cardsData) return;

       const totalUsage = formattedData?.cardsData
                            ?.reduce((sum, card) => sum + Number(card?.value || 0), 0)
                            ?.toFixed(2);

        setSelectedCards((prev) =>
            prev.map((item, index) => {
                const cardKey = Object.keys(item)[0];
                const card = item[cardKey];

                let updatedValue = 0;

                if (index === 0) {
                    updatedValue = Number(totalUsage || 0);
                }
                else {
                    const formattedCard = formattedData.cardsData[index - 1];
                    updatedValue = Number(formattedCard?.value || 0);
                }

                return {
                    [cardKey]: {
                        ...card,
                        rightValue: updatedValue + " GB"
                    }
                };
            })
        );
    }, [formattedData]);

    const options = {
        time: {
            timezone: timeZone,
        },
        chart: {
            type: "column",
            height: "400px",
            zoomType: "x",
            plotBorderWidth: 1,
            style: {
                fontFamily: "Roboto, Nunito Sans, Arial, Verdana, Helvetica, sans-serif",
            },
        },
        title: {
            text: "",
            align: "left",
            floating: true,
            x: 12,
            y: 32,
            style: {
                fontWeight: "500",
            },
        },
        lang: {
            noData: "No Data",
        },
        noData: {
            style: {
                fontWeight: "bold",
                fontSize: "15px",
                color: "#303030",
            },
        },
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
        plotOptions: {
            column: {
                stacking: "normal",
                dataLabels: {
                    enabled: false,
                },
            },
        },
        tooltip: {
            shared: true,
            pointFormat:
                '<span style="color:{series.color}">{series.name}</span>: <b>{point.y} GB</b><br/>',
            valueDecimals: 2,
            borderWidth: 1,
        },
        xAxis: {
            gridLineWidth: 0.5,
            type: "datetime",
            min: moment(startDate).utc().toDate().valueOf(),
            max: moment(endDate).utc().toDate().valueOf(),
            tickInterval: 24 * 3600 * 1000,
        },
        yAxis: {
            // tickInterval: 50,
            gridLineWidth: 1,
            labels: {
                format: "{value} GB",
            },
            title: {
                text: null,
            },
        },
        exporting: {
            enabled: false,
            csv: {
                columnHeaderFormatter: (item, key) => {
                    if (item.isXAxis) {
                        return 'Time';
                    }
                    return `${item.name.replace(" GB", "")} (GB)`;
                }
            }
        },
        series: series,
    };

    const chartRef = useRef<HighchartsReact.RefObject>(null);

    const handleDownloadCSV = () => {
        if (chartRef.current?.chart) {
            const csvData = chartRef.current.chart.getCSV();
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'starlink_usage_data.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            console.error('Chart reference is null or not initialized.');
        }
    };


    const filterPopClick = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorElRef(e.currentTarget);
        setFilterPopup(true);
      };

    return (
        <>
            <Grid mt={1}>
                <AnalyticsCardContents cardsData={selectedCards} />
            </Grid>

            <Paper elevation={1} className="starlink--reports--usage_chart--container" classes={{ root: 'starlink--base--padding_05x' }}>
                <div style={{ display: 'flex', justifyContent: "space-between" }}>
                    <div>
                        {filterPopup && (
                        <Menu
                            open={filterPopup}
                            className="rc--filter_menu_container_dashboard menuScroll strMaps-filter"
                            anchorEl={anchorElRef}

                        >
                            <Grid className="sitesFilterHeaderMargin">
                                <div className="filter-header">Filters</div>
                                <img
                                    src={close}
                                    onClick={() => setFilterPopup(false)}
                                    alt="Close"
                                    className="activityReportsFilter-close"
                                />
                            </Grid>
                            <Grid className="filter-divider-width">
                                <Divider className="filter-border-color" />
                            </Grid>
                            <Grid className="usageFilterComponent">
                                <div className="Form">
                                    <Grid className="am-table">
                                        <Grid className="dash-filter-pop">
                                            <Grid>
                                                <Grid className="sitesFiltersPadding">
                                                    <Grid className="internetr-status-header-paddingBottom">
                                                        <span className="internet-status-header">Billing Usage</span>
                                                    </Grid>
                                                        <RadioGroup
                                                            name="filterOptions"
                                                            value={selectedFilters} // assume selectedFilter is a single string state
                                                            onChange={(e) => handleFilterChange(e.target.value)}
                                                            className="filterComponents"
                                                        >
                                                            {defaultFilters?.map((status) => (
                                                                <FormControlLabel
                                                                    key={`form-filter-${status.label}`}
                                                                    className="sitesInternetStatusOptions starlinkMap-filterPopup"
                                                                    value={status.label}
                                                                    control={<Radio />}
                                                                    label={status.name}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                            <Grid className="apply-filter-divider-width">
                                <Divider className="filter-border-color" />
                            </Grid>
                            <div className="clearFiletrBtnDiv margin-left-filter">
                                <Button className="clearFiletrBtnUsage padding-right-0" onClick={() => setFilterPopup(false)}>
                                    Cancel
                                </Button>
                                <Grid className="dash-filter-pop">
                                    <Button className="usage-applyFilterBtn" onClick={() => setFilterPopup(false)}>
                                        Apply Filter
                                    </Button>
                                </Grid>
                            </div>
                        </Menu>
                    )}
                    </div>
                    <div className="alignTopbarItemsCenter">
                        <FormControlLabel
                            className="margin-left-auto"
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!includeInactiveServiceLines}
                                    onChange={(e) => onIncludeInactiveServiceLinesChange?.(e.target.checked)}
                                />
                            }
                            label="Include inactive service lines"
                        />
                        <div className="download-summary-btn" onClick={handleDownloadCSV}><img className="downloadImg" src={Download} alt="" /><Button>Download</Button></div>
                    </div>
                </div>
                <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} immutable={false} />
            </Paper>
        </>
    );
};

const mapStateToProps = (state) => ({
    assetUsage: state.starlinkReports.assetUsage,
    assetUsageSplit: state.starlinkReports.assetUsageSplit,
    userTimezone: state.authReducer.userTimezone,
    newSummaryServiceLine: state?.authReducer?.newSummaryServiceLine
});

export default withRouter(connect(mapStateToProps, {})(AssetUsageBarChart));